import React, {useState, useEffect} from 'react'
import {usePageDesigner, useAnalytics} from '_customHooks'
import {Stack, Link, useMultiStyleConfig, Text} from '@chakra-ui/react'
import {Picture} from '_uiComponents'
import {getAbsoluteUrl} from '_utils'
import {useGlobalConfigState} from '_context'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

/**
 * @name Widget
 *
 * @component
 *
 * @description
 * Renders the widget.
 *
 * @remarks
 * The content is retrieved via page designer, see the mapping function below for more informations.
 *
 * @returns {React.FC} - Rendered widget component.
 */

interface WidgetContents {
    analytics_label?: string
    background_color?: string
    background_color_collapsed?: string
    text_color?: string
    text_color_collapsed?: string
    border_color?: string
    border_color_collapsed?: string
    border_radius?: string
    border_weight?: string
    border_weight_collapsed?: string
    cld_icon?: any
    cld_image_mobile?: any
    cld_image_tablet?: any
    cld_image_desktop?: any
    image_link?: string
    image_title?: string
    widget_max_width?: string
    widget_max_width_mobile?: string
    widget_ratio?: string
    collapsed_text?: string
    class_name?: string
    custom_css?: string
    preventRendering?: boolean
}

interface WidgetProps {
    forceHidden?: boolean
    expandedWidget?: boolean
    categoryId?: string
}

const Widget: React.FC<WidgetProps> = ({forceHidden = false, expandedWidget = false, categoryId}) => {
    const {appPreferences} = useGlobalConfigState()
    const {locale, site} = useMultiSite()
    const {hostLink} = appPreferences
    const hostLinkLocalized = (hostLink ? JSON.parse(hostLink)[site.id] : undefined) as string
    const {pageComponents} = usePageDesigner({pageId: 'widget-callimacus'})
    const widgetContents = extractWidgetContents(pageComponents)

    const {analyticsEventWidget} = useAnalytics()

    const [expanded, setExpanded] = useState(expandedWidget)
    const styles = useMultiStyleConfig('Widget', {
        backgroundColor: widgetContents.background_color,
        backgroundColorCollapsed: widgetContents.background_color_collapsed,
        borderRadius: widgetContents.border_radius,
        borderWidth: widgetContents.border_weight,
        borderWidthCollapsed: widgetContents.border_weight_collapsed,
        maxWidth: widgetContents.widget_max_width,
        maxWidthMobile: widgetContents.widget_max_width_mobile,
        expanded: expanded,
        ratio: widgetContents.widget_ratio,
        textColor: widgetContents.text_color,
        textColorCollapsed: widgetContents.text_color_collapsed,
        borderColor: widgetContents.border_color,
        borderColorCollapsed: widgetContents.border_color_collapsed,
    }) as Record<string, any>

    useEffect(() => {
        analyticsEventWidget({
            eventName: 'custom_event',
            category: 'callimacus_widget',
            action: 'show',
            label: expandedWidget ? 'big' : 'small',
        })
    }, [analyticsEventWidget])

    useEffect(() => {
        if (typeof window === 'undefined') return

        let footerObserver: IntersectionObserver | null = null
        let mutationObserver: MutationObserver | null = null

        const observeFooter = (footer: HTMLElement) => {
            footerObserver = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setExpanded(false)
                    }
                },
                {threshold: 0.1},
            )
            footerObserver.observe(footer)
        }

        const existingFooter = document.querySelector<HTMLElement>('footer[data-testid="footer"]')
        if (existingFooter) {
            observeFooter(existingFooter)
        } else {
            mutationObserver = new MutationObserver(() => {
                const footer = document.querySelector<HTMLElement>('footer[data-testid="footer"]')
                if (footer) {
                    mutationObserver?.disconnect()
                    mutationObserver = null
                    observeFooter(footer)
                }
            })
            mutationObserver.observe(document.body, {childList: true, subtree: true})
        }

        return () => {
            footerObserver?.disconnect()
            mutationObserver?.disconnect()
        }
    }, [])

    const widgetHref = buildWidgetHref(getAbsoluteUrl(hostLinkLocalized, locale.alias, widgetContents.image_link || '/'), categoryId)

    return widgetContents.preventRendering ? (
        <></>
    ) : (
        <>
            {widgetContents.custom_css && <style>{widgetContents.custom_css}</style>}
            {!forceHidden && (
                <Stack {...styles.widgetWrapper} data-testid="widget" className={'cc-widget-box ' + widgetContents.class_name || ''}>
                    <Link
                        data-testid="widget"
                        {...styles.widgetLink}
                        href={widgetHref}
                        isExternal={false}
                        onClick={() => {
                            analyticsEventWidget({
                                eventName: 'custom_event',
                                category: 'callimacus_widget',
                                action: 'click',
                                label: 'big',
                            })
                        }}
                    >
                        <Picture
                            overflow="hidden"
                            objectFit="cover"
                            imagexs={widgetContents?.cld_image_mobile?.imageUrl}
                            imagemd={widgetContents?.cld_image_tablet?.imageUrl || widgetContents?.cld_image_desktop?.imageUrl}
                            imagexl={widgetContents?.cld_image_desktop?.imageUrl}
                        />

                        {widgetContents.image_title ? (
                            <Stack {...styles.widgetTextWrapper}>
                                <Text {...styles.widgetText} align="center">
                                    {widgetContents.image_title}
                                </Text>
                            </Stack>
                        ) : null}
                    </Link>
                    <Stack
                        {...styles.widgetButton}
                        aria-label={'Open widget'}
                        onClick={() => {
                            setExpanded(true)
                            analyticsEventWidget({
                                eventName: 'custom_event',
                                category: 'callimacus_widget',
                                action: 'expand',
                                label: 'small',
                            })
                        }}
                        data-testid="widget-button"
                        role="button"
                    >
                        <Stack {...styles.widgetButtonIconWrapper}>
                            {widgetContents?.cld_icon?.imageUrl && <Picture {...styles.widgetButtonIcon} imagexs={widgetContents.cld_icon.imageUrl} />}
                        </Stack>
                        <Stack {...styles.widgetButtonTextWrapper}>
                            {widgetContents.collapsed_text ? <Text {...styles.widgetCollapsedText}>{widgetContents.collapsed_text}</Text> : null}
                        </Stack>
                    </Stack>
                </Stack>
            )}
        </>
    )
}

function extractWidgetContents(pageComponents): WidgetContents {
    if (!pageComponents || !pageComponents.regions || !pageComponents.regions[0].components) return {preventRendering: true}
    return pageComponents.regions.flatMap((region) => region.components.map((component) => component.data)).reduce((acc, data) => ({...acc, ...data}), {})
}

function buildWidgetHref(baseHref: string, categoryId?: string): string {
    if (!categoryId) return baseHref

    try {
        const isAbsoluteUrl = /^https?:\/\//i.test(baseHref)
        const parsedUrl = new URL(baseHref, isAbsoluteUrl ? undefined : 'http://localhost')

        parsedUrl.searchParams.set('categories', categoryId)

        if (isAbsoluteUrl) {
            return parsedUrl.toString()
        }

        return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
    } catch {
        return baseHref
    }
}

export {Widget}
